import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, WhatsappLogo, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { id: "novo", label: "Novo", cls: "status-analise" },
  { id: "em_contato", label: "Em Contato", cls: "status-pendente" },
  { id: "convertido", label: "Convertido", cls: "status-ativo" },
  { id: "perdido", label: "Perdido", cls: "status-inadimplente" },
];
const STATUS_MAP = Object.fromEntries(STATUS_OPTS.map((s) => [s.id, s]));
const PLAN_LABEL = { basico: "Básico", plus: "Fit", ultra: "Ultra" };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const url = filter === "todos" ? "/leads" : `/leads?status=${filter}`;
    const { data } = await api.get(url);
    setLeads(data);
  };
  useEffect(() => { load(); }, [filter]);

  const changeStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    toast.success("Status atualizado");
    load();
    if (detail?.lead_id === id) setDetail({ ...detail, status });
  };

  const counts = STATUS_OPTS.reduce((acc, s) => {
    acc[s.id] = leads.filter((l) => l.status === s.id).length;
    return acc;
  }, {});

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-8">
        <div className="eyebrow mb-1">Simulações públicas</div>
        <h1 className="font-display text-4xl tracking-tight">Leads</h1>
        <p className="text-slate-600 mt-2">Cada simulação feita na landing pública vira um lead. Entre em contato e converta em cliente.</p>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-6">
        {STATUS_OPTS.map((s) => (
          <button key={s.id} onClick={() => setFilter(s.id)} data-testid={`leads-tab-${s.id}`}
            className={`card-flat p-5 text-left transition-all ${filter === s.id ? "border-[var(--emerald)] border-2" : ""}`}>
            <div className="eyebrow">{s.label}</div>
            <div className="metric-value text-3xl mt-2">{counts[s.id] || 0}</div>
          </button>
        ))}
      </div>

      <div className="mb-3 flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setFilter("todos")}>Ver todos ({leads.length})</Button>
      </div>

      <div className="card-flat overflow-hidden" data-testid="leads-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 eyebrow">
            <tr>
              <th className="text-left p-4">Nome</th>
              <th className="text-left p-4">Contato</th>
              <th className="text-left p-4">Aluguel</th>
              <th className="text-left p-4">Plano</th>
              <th className="text-left p-4">Prêmio</th>
              <th className="text-left p-4">Data</th>
              <th className="text-left p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.lead_id} className="border-t border-[var(--line)] hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(l)}>
                <td className="p-4 font-medium">{l.name}<div className="text-[11px] text-slate-400 mono">{l.cpf}</div></td>
                <td className="p-4">
                  <div className="text-xs">{l.email}</div>
                  <div className="text-xs text-slate-500 mono">{l.phone}</div>
                </td>
                <td className="p-4 mono">{fmtBRL(l.rent_amount)}</td>
                <td className="p-4">{PLAN_LABEL[l.plan]}</td>
                <td className="p-4 mono">{fmtBRL(l.monthly_premium)}</td>
                <td className="p-4 text-slate-500">{fmtDate(l.created_at)}</td>
                <td className="p-4"><span className={`status-pill ${STATUS_MAP[l.status]?.cls || ""}`}>{STATUS_MAP[l.status]?.label || l.status}</span></td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan="7" className="p-10 text-center text-slate-400">Sem leads nesta categoria. Compartilhe o link do simulador para atrair inquilinos!</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="lead-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl">{detail.name}</DialogTitle>
                <div className="text-sm text-slate-500 mono">{detail.cpf}</div>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Info label="Aluguel" value={fmtBRL(detail.rent_amount)} />
                <Info label="Plano" value={PLAN_LABEL[detail.plan]} />
                <Info label="Prêmio/mês" value={fmtBRL(detail.monthly_premium)} />
                <Info label="Cobertura" value={fmtBRL(detail.coverage_total)} />
              </div>
              <div className="mt-4">
                <div className="eyebrow mb-2">Contato</div>
                <div className="text-sm">{detail.email}</div>
                <div className="text-sm mono">{detail.phone}</div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <a data-testid="lead-whatsapp"
                  href={`https://wa.me/55${(detail.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Olá ${detail.name}! Somos da Safimob. Vi sua simulação — plano ${PLAN_LABEL[detail.plan]} com prêmio de ${detail.monthly_premium.toLocaleString("pt-BR", {style:"currency",currency:"BRL"})}/mês. Podemos conversar sobre a garantia locatícia?`)}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#25D366] text-white text-sm hover:opacity-90">
                  <WhatsappLogo size={16} weight="fill" /> WhatsApp
                </a>
                <a data-testid="lead-email" href={`mailto:${detail.email}?subject=Sua simulação Safimob&body=Olá ${detail.name}, vi sua simulação e gostaria de conversar sobre a garantia locatícia Safimob.`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-[var(--line)] text-sm hover:bg-slate-50">
                  <EnvelopeSimple size={16} /> Email
                </a>
                <a data-testid="lead-phone" href={`tel:${detail.phone}`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-[var(--line)] text-sm hover:bg-slate-50">
                  <Phone size={16} /> Ligar
                </a>
              </div>
              <div className="mt-4">
                <div className="eyebrow mb-2">Atualizar status</div>
                <Select value={detail.status} onValueChange={(v) => changeStatus(detail.lead_id, v)}>
                  <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Info({ label, value }) {
  return <div className="p-3 rounded-md bg-slate-50 border border-slate-100"><div className="eyebrow">{label}</div><div className="metric-value text-lg mt-1">{value}</div></div>;
}
